import { TicketBasket } from "@shared/modules/basket/types";
import Repository, {
  ExtendedAxiosRequestConfig,
} from "@shared/services/api/abstracts/Repository";
import { UrlRouteMap, PaginatorContract } from "@shared/services/api/types";
import { venueBasedUrl } from "@shared/services/api/utils/RepositoryUrlUtils";
import {
  VenueBasedSquareIntegrationsSerializer,
  VenueBasedSquareOAuthLocationSerializer,
  VenueBasedSquareOAuthTerminalCheckoutCancelSerializer,
  VenueBasedSquareOAuthTerminalSerializer,
} from "../types/square";

export default class VenueBasedSquareRepository extends Repository<VenueBasedSquareIntegrationsSerializer> {
  public readonly repositoryName = "VenueBasedSquareRepository";

  public readonly endpoint = venueBasedUrl("integrations/square/");

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      getLocationInfo: "location-info",
      getLocationsList: "locations-list",
      revokeAccess: "revoke-access",
      getPairedDeviceCode: "paired-device-code",
      generateDeviceCode: "device-code",
      unpairDeviceCode: "unpair-device-code",
      cancelCheckout: "checkout/{terminalCheckoutId?}/cancel",
      getOAuthEnabled: "is-oauth-enabled",
    };
  }

  public async getLocationsList(): Promise<
    PaginatorContract<VenueBasedSquareOAuthLocationSerializer>
  > {
    const { data } = await this.request<
      PaginatorContract<VenueBasedSquareOAuthLocationSerializer>
    >(this.buildUrl("getLocationsList"));
    return data;
  }

  public async getLocationInfo(
    config?: ExtendedAxiosRequestConfig
  ): Promise<VenueBasedSquareOAuthLocationSerializer> {
    const { data } =
      await this.request<VenueBasedSquareOAuthLocationSerializer>(
        this.buildUrl("getLocationInfo"),
        config
      );
    return data;
  }

  public async revokeAccess(): Promise<void> {
    const { data } = await this.request<void>(this.buildUrl("revokeAccess"), {
      method: "GET",
    });
    return data;
  }

  public async getPairedDeviceCode(): Promise<
    VenueBasedSquareOAuthTerminalSerializer | Record<string, never>
  > {
    const { data } = await this.request<
      VenueBasedSquareOAuthTerminalSerializer | Record<string, never>
    >(this.buildUrl("getPairedDeviceCode"));
    return data;
  }

  public async generateDeviceCode(
    venueId: number
  ): Promise<VenueBasedSquareOAuthTerminalSerializer> {
    const { data } =
      await this.request<VenueBasedSquareOAuthTerminalSerializer>(
        this.buildUrl("generateDeviceCode", { venueId }),
        {
          method: "POST",
          data: { venue: venueId },
        }
      );
    return data;
  }

  public async unpairDeviceCode(
    venueId: number
  ): Promise<Partial<Record<symbol, never>>> {
    const { data } = await this.request<Partial<Record<symbol, never>>>(
      this.buildUrl("unpairDeviceCode", { venueId }),
      {
        method: "POST",
        data: { venue: venueId },
      }
    );
    return data;
  }

  public async cancelCheckout(
    terminalCheckoutId: TicketBasket["square_terminal_checkout_id"],
    venue: number | undefined
  ): Promise<VenueBasedSquareOAuthTerminalCheckoutCancelSerializer> {
    const { data } =
      await this.request<VenueBasedSquareOAuthTerminalCheckoutCancelSerializer>(
        this.buildUrl("cancelCheckout", { terminalCheckoutId }),
        {
          method: "POST",
          data: { id: terminalCheckoutId, venue },
        }
      );
    return data;
  }

  public async getOAuthEnabled(): Promise<boolean> {
    const { data } = await this.request<boolean>(
      this.buildUrl("getOAuthEnabled")
    );
    return data;
  }
}
